import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, Package, FolderTree } from 'lucide-react';
import { apiClient } from '../../lib/api';

interface ResourcePickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (id: string) => void;
  resourceType: 'product' | 'category';
}

export const ResourcePickerModal: React.FC<ResourcePickerModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  resourceType,
}) => {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchItems();
    }
  }, [isOpen, resourceType]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const endpoint = resourceType === 'product' ? '/products' : '/products/categories';
      const res = await apiClient.get<any>(endpoint);
      
      if (res?.data?.products) {
        setItems(res.data.products);
      } else if (res?.data) {
        setItems(res.data);
      } else if (Array.isArray(res)) {
        setItems(res);
      } else if (res?.products) {
        setItems(res.products);
      }
    } catch (err) {
      console.error(`Failed to fetch ${resourceType}s:`, err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const title = resourceType === 'product' ? 'Select Product' : 'Select Category';
  const Icon = resourceType === 'product' ? Package : FolderTree;

  return createPortal(
    <div className="fixed inset-0 z-[99990] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fadeIn">
      <div className="w-full max-w-2xl bg-white border border-slate-200 shadow-2xl rounded-lg overflow-hidden flex flex-col max-h-[85vh] animate-scaleUp">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50/50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#006838]/10 rounded-md">
              <Icon className="w-5 h-5 text-[#006838]" />
            </div>
            <div>
              <h2 className="font-bold text-slate-800 text-lg">{title}</h2>
              <p className="text-xs text-slate-500 font-medium">Choose a {resourceType} to link to this section</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 hover:bg-slate-100 p-2 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 bg-slate-50">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-48 space-y-3">
              <Loader2 className="w-8 h-8 text-[#006838] animate-spin" />
              <p className="text-sm font-medium text-slate-500">Loading {resourceType}s...</p>
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 border-2 border-dashed border-slate-200 rounded-xl bg-white space-y-4">
              <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center">
                <Icon className="w-6 h-6 text-slate-400" />
              </div>
              <div className="text-center">
                <h3 className="font-semibold text-slate-700">No {resourceType}s found</h3>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {items.map((item) => (
                <div
                  key={item.id}
                  onClick={() => onSelect(item.id)}
                  className="flex items-center gap-4 p-3 rounded-lg border border-slate-200 bg-white hover:border-[#006838] hover:shadow-sm cursor-pointer transition-all"
                >
                  {(item.images?.[0]?.url || item.image) ? (
                    <img src={item.images?.[0]?.url || item.image} alt={item.name} className="w-12 h-12 object-cover rounded-md border border-slate-100" />
                  ) : (
                    <div className="w-12 h-12 bg-slate-100 rounded-md flex items-center justify-center border border-slate-200">
                      <Icon className="w-5 h-5 text-slate-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-800 text-sm truncate">{item.name || item.title}</div>
                    {item.price && <div className="text-xs text-slate-500 mt-0.5">${Number(item.price).toFixed(2)}</div>}
                  </div>
                  <button className="px-3 py-1.5 text-xs font-semibold text-[#006838] bg-[#006838]/10 rounded-md">
                    Select
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};
